// Type-only for the same reason as in cinema-day.ts: this module must stay resolvable without
// node_modules, and `locale-helper` is not.
import type { TimeZone } from './locale-helper';

/**
 * Converting between an instant and a cinema's wall clock.
 *
 * A `Date` is an instant; its `getHours()`/`setHours()` read and write in the *browser's* zone. An
 * operator in London and one in Berlin therefore see and enter different clock times for the same
 * screening — and a cinema always works in its own local time, so both of those cannot be right.
 * Everything below is the pair of conversions that lets UI code keep using plain `Date` while
 * meaning "the cinema's clock".
 *
 * Offsets are derived per instant rather than assumed, so the DST changeovers are handled: a
 * cinema in `Europe/Berlin` is +01:00 in January and +02:00 in July, and a fixed offset would put
 * every summer screening an hour out.
 */

/**
 * A `Date` whose *local* fields spell out the wall clock in `timeZone` at that instant.
 *
 * The result is deliberately NOT a valid instant — it is shifted so `getHours()` and friends
 * return the cinema's clock. Use it to read and display; never persist it or send it to the API.
 * {@link cinemaWallClockToInstant} is the way back.
 *
 * Reading the zone's own fields is not the scenic route, it is the only correct one. Adding the
 * zone's UTC offset to the instant arithmetically looks equivalent and is not: the getters then add
 * the *browser's* offset on top, so a Berlin screening read 05:47 on a UK machine where the cinema
 * clock said 04:47 and the raw instant said 03:47 — three different times for one screening.
 *
 * Known limit: on the night the *browser's* zone springs forward, a cinema time falling inside the
 * skipped local hour has no representation here and the `Date` constructor rolls it forward an
 * hour. One hour a year, only for viewers whose own zone jumps at a different moment than the
 * cinema's. Representing it would mean abandoning `Date` for the pickers this feeds, which read
 * and write through local getters.
 */
export function instantToCinemaWallClock(instant: Date | string, timeZone: TimeZone): Date {
    // `en-CA` for stable ISO-ish parts, and `hour12: false` can yield hour "24" — hence the modulo.
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).formatToParts(new Date(instant));

    const partValue = (type: Intl.DateTimeFormatPartTypes): number =>
        Number(parts.find((part) => part.type === type)?.value ?? 0);

    return new Date(
        partValue('year'),
        partValue('month') - 1,
        partValue('day'),
        partValue('hour') % 24,
        partValue('minute'),
        partValue('second')
    );
}

/**
 * How far {@link instantToCinemaWallClock} moves a given instant — a whole number of minutes, since
 * that is all a UTC offset can be.
 *
 * Measured on a second-truncated instant because the formatter has no millisecond field: taking the
 * difference directly would fold the discarded milliseconds into the "offset" and shift every
 * conversion by up to 999ms. That is enough to matter — the Cinema Day ends at 03:59:59.999, and a
 * boundary drifting past it re-opens the nightly gap CIN-14663 closed.
 */
function wallClockShiftMs(instant: Date, timeZone: TimeZone): number {
    const wholeSecond = new Date(Math.floor(instant.getTime() / 1000) * 1000);
    return instantToCinemaWallClock(wholeSecond, timeZone).getTime() - wholeSecond.getTime();
}

/**
 * The instant at which `timeZone`'s wall clock reads the local fields of `wallClock` — the inverse
 * of {@link instantToCinemaWallClock}, and what an edited value must be turned into before it is
 * saved.
 *
 * The shift is resolved twice because the first lookup uses the wrong instant: to know the shift we
 * need the instant, and to know the instant we need the shift. One correction settles it everywhere
 * except the hour DST skips or repeats, where no exact answer exists — a repeated 02:30 resolves to
 * its second occurrence.
 *
 * Seconds and milliseconds do not survive the round trip. Screening times are minute-aligned, and
 * the pickers built on this only ever offer minutes.
 */
export function cinemaWallClockToInstant(wallClock: Date, timeZone: TimeZone): Date {
    const firstGuess = new Date(wallClock.getTime() - wallClockShiftMs(wallClock, timeZone));
    return new Date(wallClock.getTime() - wallClockShiftMs(firstGuess, timeZone));
}
